import { Children, cloneElement, FC, PropsWithChildren, ReactElement } from 'react';

import { getCombinedLinkFieldValueHref } from '@eneco/common/link';
import logger from '@eneco/common/log';
import { join } from '@eneco/common/path';
import { NavLink, TabNavigation, Tag } from '@eneco/sparky';
import { wrap } from '@sitecore/common';
import { LinkFieldValue } from '@sitecore-jss/sitecore-jss-react';
import NextLink from 'next/link';
import { isElement } from 'react-is';

import { CMS_QUERY_PARAM, isCmsOverride, withCmsParam } from './cmsOverride';
import { useRouter } from './useRouter';

type HrefOrLinkValue =
  | { href: string; linkValue?: never }
  | { href?: never; linkValue: Pick<LinkFieldValue, 'anchor' | 'href' | 'querystring' | 'target'> };

type LinkProps = {
  className?: never;
  currentMatch?: 'exact';
  editable?: string;
  linkType: string;
} & HrefOrLinkValue;

// TODO Sparky could perhaps export something like an enum to identify components featuring the `isCurrent` prop
const hasIsCurrent = new Set([NavLink.toString(), TabNavigation.Item.toString(), Tag.toString()]);
const supportsIsCurrent = (component: ReactElement): boolean => {
  const componentProps = component.props as Record<string, unknown>;
  return typeof componentProps.isCurrent !== 'boolean' && hasIsCurrent.has(component.type.toString());
};

const IS_HOSTED_PR_REGULAR_EXPRESSION = /^\/pr-\d+\//;

export const Link: FC<PropsWithChildren<LinkProps>> = props => {
  const router = useRouter();

  if (props.editable) {
    const { editable, ...rest } = props;
    return wrap(props, <Link {...rest} />, { a: props.children });
  }

  const { basePath, routerBasePath, activePath } = router;
  const { children, currentMatch, className, href: _href, linkType, linkValue, ...rest } = props;

  const href = _href ?? getCombinedLinkFieldValueHref(linkValue);
  const { target } = linkValue || {};

  if (!href) {
    logger.warn(`Xo9K80`, `Link component is missing href`, props);
    return children;
  }

  const isAbsolute = href.startsWith('http') || href.startsWith('tel') || href.startsWith('mailto');
  const isAnchor = href.startsWith('#');

  const path = isAnchor ? href : href.replace(/(https?:\/\/)?([^/]+)?(.+)?/, '$3');
  const pathname = routerBasePath && !path.startsWith(routerBasePath) ? join(routerBasePath, path) : path;

  const onlyChild = Children.only(children);
  // Sanity-migration-only, see `cmsOverride.ts`.
  const cmsOverride = isCmsOverride(router.query[CMS_QUERY_PARAM]) ? router.query[CMS_QUERY_PARAM] : undefined;

  if (isElement(onlyChild)) {
    // Stay within container (base path) using client-side routing
    if (linkType !== 'external' && !isAbsolute) {
      const isCurrent = currentMatch === 'exact' ? pathname === activePath : activePath.startsWith(pathname);
      const childprops = { ...(className !== undefined && { className }), ...(isCurrent && supportsIsCurrent(onlyChild) && { isCurrent }) };
      const child = cloneElement(onlyChild, childprops);

      // When running in a Hosted PR container, the basepath is prefixed with /pr-<pr-number>/
      // To fix links in the PR container, we need to remove the prefix from the pathname
      let basepathWithoutHostedPr = basePath;
      const isHostedPr = IS_HOSTED_PR_REGULAR_EXPRESSION.test(basePath);
      const pathnameIncludesHostedPr = IS_HOSTED_PR_REGULAR_EXPRESSION.test(pathname);
      if (isHostedPr && !pathnameIncludesHostedPr) {
        basepathWithoutHostedPr = basePath.replace(IS_HOSTED_PR_REGULAR_EXPRESSION, '/');
      }
      const path = (basepathWithoutHostedPr ? pathname.replace(basepathWithoutHostedPr, '') : pathname) || '/';
      const pathWithCms = cmsOverride && !isAnchor ? withCmsParam(path, cmsOverride) : path;

      return (
        <NextLink href={pathWithCms} passHref legacyBehavior locale={false} target={target}>
          {child}
        </NextLink>
      );
    }

    // Container hopping: full page load. Only forward the override for same-site links (not `linkType === 'external'`
    // or tel/mailto), since we can't verify a true external target belongs to a container that honors `_cms`.
    const hrefWithCms =
      cmsOverride && isAbsolute && linkType !== 'external' && !href.startsWith('tel') && !href.startsWith('mailto')
        ? withCmsParam(href, cmsOverride)
        : href;

    return cloneElement(onlyChild as ReactElement<{ className?: string; target?: string; href?: string }>, {
      href: hrefWithCms,
      ...(className !== undefined && { className }),
      target: (onlyChild.props as { target?: string }).target || target,
    });
  }

  // Unreachable: Children.only above throws unless `children` is itself a valid element, so
  // isElement(onlyChild) is always true (pre-existing, left as-is).
  return (
    <a href={href} className={className} {...rest} target={target}>
      {children}
    </a>
  );
};
